import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AlertConfig = ({ pageId }) => {
  const [alertText, setAlertText] = useState('');
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/pages/${pageId}`);
        setAlertText(response.data.alert_text || '');
        setAlertEnabled(response.data.alert_enabled || false);
      } catch (error) {
        console.error('Failed to load alert config:', error);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [pageId]);

  const saveAlert = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, {
        alert_text: alertText,
        alert_enabled: alertEnabled
      });
      toast.success(alertEnabled ? 'Alert is now live on displays' : 'Alert saved (currently off)');
    } catch (error) {
      toast.error('Failed to save alert');
    } finally {
      setSaving(false);
    }
  };

  const toggleAlert = async (enabled) => {
    setAlertEnabled(enabled);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, {
        alert_text: alertText,
        alert_enabled: enabled
      });
      toast.success(enabled ? 'Alert is now live!' : 'Alert turned off');
    } catch (error) {
      setAlertEnabled(!enabled);
      toast.error('Failed to toggle alert');
    }
  };

  if (loading) return null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="alert-config">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" /> Announcement Bar
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Display an urgent message overlay on the slideshow
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm ${alertEnabled ? 'text-amber-400' : 'text-slate-500'}`}>
              {alertEnabled ? 'LIVE' : 'Off'}
            </span>
            <Switch
              checked={alertEnabled}
              onCheckedChange={toggleAlert}
              data-testid="alert-toggle"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Input
            type="text"
            value={alertText}
            onChange={(e) => setAlertText(e.target.value)}
            placeholder="e.g., Building closed today due to weather"
            className="bg-slate-950 border-slate-700 text-white flex-1"
            data-testid="alert-text-input"
          />
          <Button
            onClick={saveAlert}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600 text-black"
            data-testid="alert-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
        {alertEnabled && alertText && (
          <div className="mt-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <p className="text-amber-400 text-sm font-medium">Preview:</p>
            <p className="text-white text-sm mt-1">{alertText}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertConfig;
